/* eslint-disable max-lines */
import {
    Search, Filter, Trash2, Save, RefreshCw, Plus
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu';
import {
    COLUMN_CONFIGS
} from './constants';
import {
 Field
} from './types/builderFields.types';
interface FieldsHeaderProps {
    searchTerm: string;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    filterColumn: string | null;
    setFilterColumn: React.Dispatch<React.SetStateAction<string | null>>;
    filterValue: string;
    setFilterValue: React.Dispatch<React.SetStateAction<string>>;
    selectedFields: Field[]; // Assuming selectedFields is an array of objects with an id
    isDeleting: boolean;
    newFieldsCount: number;
    saveSchema: () => void;
    isSaving: boolean;
    isAddingMode: boolean;
    setFieldsToDelete: React.Dispatch<React.SetStateAction<Field[]>>;
    setDelModal: (open: boolean) => void;
    onRefresh: () => void;
    handleAddField: () => void;
}

export const FieldsHeader: React.FC<FieldsHeaderProps> = ({
    searchTerm,
    setSearchTerm,
    filterColumn,
    setFilterColumn,
    filterValue,
    setFilterValue,
    selectedFields,
    isDeleting,
    newFieldsCount,
    saveSchema,
    isSaving,
    isAddingMode,
    setFieldsToDelete,
    setDelModal,
    onRefresh,
    handleAddField
}) => {
    return (
        <div className="flex items-center justify-between gap-2 border-b p-3 bg-muted/30">
            <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search fields..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 pr-4"
                    />
                </div>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                        </TooltipTrigger>
                        <TooltipContent>Clear search</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={handleAddField}
                >
                    <Plus className="h-4 w-4" />
                    <span>Add field</span>
                </Button>
                <FilterDropdown filterColumn={filterColumn} setFilterColumn={setFilterColumn} setFilterValue={setFilterValue} />

                {filterColumn && (
                    <Input
                        placeholder="Filter value..."
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        className="w-40"
                    />
                )}

                {/* REFRESH BUTTON */}
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-1"
                                onClick={onRefresh}
                            >
                                <RefreshCw className="h-4 w-4" />
                                <span>Refresh</span>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Refresh fields</TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                {selectedFields.length > 0 && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    className="gap-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFieldsToDelete(selectedFields);
                                        setDelModal(true);
                                    }}
                                    disabled={isDeleting}
                                >
                                    <Trash2 className="h-4 w-4" />
                                    <span>{isDeleting ? 'Deleting...' : `Delete Selected (${selectedFields.length})`}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete selected fields</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}

                {(newFieldsCount > 0) && isAddingMode && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="default"
                                    size="sm"
                                    className="gap-1"
                                    onClick={saveSchema}
                                    disabled={isSaving}
                                >
                                    <Save className="h-4 w-4" />
                                    <span>
                                        {isSaving
                                            ? 'Adding...'
                                            : `Add Fields (${newFieldsCount})`}
                                    </span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Save new fields</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
        </div>
    );
};

interface FilterDropdownProps {
    filterColumn: string | null;
    setFilterColumn: React.Dispatch<React.SetStateAction<string | null>>;
    setFilterValue: React.Dispatch<React.SetStateAction<string>>;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
    filterColumn, setFilterColumn, setFilterValue
}) => {
    return (
        <DropdownMenu>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-1">
                                <Filter className="h-4 w-4" />
                                <span>Filter</span>
                                {filterColumn && <span className="ml-1 text-xs bg-primary/20 px-1 rounded">1</span>}
                            </Button>
                        </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Filter fields by column</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent>
                <DropdownMenuLabel>Filter by column</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {filterColumn && (
                    <DropdownMenuItem
                        onClick={() => {
                            setFilterColumn(null);
                            setFilterValue('');
                        }}
                    >
                        Clear filter
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {COLUMN_CONFIGS.map((config) => (
                    <DropdownMenuCheckboxItem
                        key={config.id}
                        checked={filterColumn === config.id}
                        onCheckedChange={() => setFilterColumn(config.id)}
                    >
                        {config.label}
                    </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
